import React, { useMemo, useState } from 'react';
import {
    ArrowRight,
    BarChart3,
    Calendar,
    Clock,
    Download,
    Edit,
    GitMerge,
    Mail,
    MapPin,
    Plus,
    Target,
    Trash2,
    TrendingUp,
    Upload,
    User,
    UserPlus,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { Link, useNavigate } from 'react-router-dom';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { ConditionalPhoneField } from '@/components/integrations/ConditionalPhoneField';
import { countries, getCountryByCode } from '@/data/countries';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';

// Import our new components and hooks
import LeadForm from '../../components/leads/LeadForm';
import EnhancedImportWizard from '../../components/leads/EnhancedImportWizard';
import LeadDuplicatesDialog from '../../components/leads/LeadDuplicatesDialog';
import { Column } from '@/components/ui/data-table';
import { GenericDataTable } from '../../components/shared/tables';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useUrlState } from '@/hooks/shared';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { useConfirm } from '@/hooks/useConfirm';
import {
    buildApiParams,
    useBulkAssignLeads,
    useBulkConvertLeadsToCustomers,
    useBulkDeleteLeads,
    useBulkUpdateLeadStatus,
    useCreateLead,
    useDeleteLead,
    useLeads,
} from '../../api/leads/use-leads';
import { useExportLeads } from '../../api/export/use-export';
import { CreateLeadRequest, Lead } from '@/models/lead.ts';
import { useActiveLeadStatuses } from '../../api/lead-statuses/use-lead-statuses';
import { useUsers } from '@/api/users/use-users.ts';
import { useCustomAttributeDefinitionsManagement } from '@/hooks/useCustomAttributes.ts';
import { usePageViews } from '@/hooks/usePageViews';
import { ViewSelector } from '@/components/shared/ViewSelector';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';

const LeadsPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const { hasPermission } = usePermissions();
    const { data: systemConfig } = useSystemConfiguration();
    const crmTimezone = systemConfig?.timezone ?? 'UTC';

    const [selectedLeads, setSelectedLeads] = useState<string[]>([]);

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
        replaceState,
        searchParams,
    } = useUrlState({
        sort: { key: 'created_at', direction: 'desc' },
        pagination: { page: 1, pageSize: 25 },
    });

    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createErrors, setCreateErrors] = useState<Record<string, string[]>>({});
    const [enhancedImportOpen, setEnhancedImportOpen] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingLead, setDeletingLead] = useState<Lead | null>(null);

    // Bulk action dialogs
    const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
    const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
    const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
    const [selectedStatusId, setSelectedStatusId] = useState<string>('');
    const [selectedUserId, setSelectedUserId] = useState<string>('');

    // Column visibility state - will be initialized after columns are defined

    // Filters + sort of the current view, shared by the list and the export so the
    // export always contains exactly what the list shows (across all pages)
    const viewParams = useMemo(
        () => ({
            search: filters.search,
            sort_by: sort.key,
            sort_direction: sort.direction,
            ...Object.fromEntries(
                Object.entries(filters).filter(
                    ([key, value]) =>
                        key !== 'search' &&
                        value !== undefined &&
                        value !== '' &&
                        value !== null &&
                        value !== 'all'
                )
            ),
        }),
        [filters, sort.key, sort.direction]
    );

    // API hooks
    const { data: leadsData, isLoading } = useLeads({
        page: pagination.page,
        per_page: pagination.pageSize,
        ...viewParams,
    });

    // Fetch lead statuses for dynamic filtering
    const { data: leadStatusesData } = useActiveLeadStatuses();
    const leadStatuses = leadStatusesData?.data || [];

    const { data: usersData } = useUsers({ status: 'active', per_page: 100 });
    const users = usersData?.data || [];

    // Fetch custom attribute definitions for dynamic columns
    const {
        definitions: customAttributeDefinitions,
        isLoading: isLoadingCustomAttributeDefinitions,
    } = useCustomAttributeDefinitionsManagement();

    /** Keep the exact same URL format as the row click (filter_*, sort, direction, pageSize, page). */
    const buildLeadDetailUrl = (leadId: string) => {
        const queryString = searchParams.toString();
        return `/leads/${leadId}${queryString ? `?${queryString}` : ''}`;
    };

    // Define columns for the DataTable - aligned with new Lead model structure
    // Must be defined before useColumnPreferences hook
    const columns: Column<Lead>[] = useMemo(
        () => [
            {
                id: 'customer_number',
                label: 'Lead #',
                key: 'customer_number',
                sortable: true,
                width: '100px',
                render: value => <div className="font-mono text-sm">{value || '-'}</div>,
            },
            {
                id: 'name',
                label: 'Name',
                key: 'name',
                sortable: true,
                width: '200px',
                render: (value, lead) => (
                    <Link
                        to={buildLeadDetailUrl(lead.id)}
                        className="font-medium text-foreground hover:text-primary hover:underline"
                    >
                        {value}
                    </Link>
                ),
            },
            {
                id: 'contact',
                label: 'Contact',
                key: 'email',
                sortable: true,
                width: '180px',
                render: (value, lead) => (
                    <div className="space-y-1">
                        <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{value}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <ConditionalPhoneField
                                phoneNumber={lead.phone || ''}
                                className="text-sm"
                            />
                        </div>
                        {lead.additional_phone_number && (
                            <div className="flex items-center gap-1">
                                <ConditionalPhoneField
                                    phoneNumber={lead.additional_phone_number}
                                    className="text-sm text-muted-foreground"
                                />
                            </div>
                        )}
                    </div>
                ),
            },
            {
                id: 'status',
                label: 'Status',
                key: 'status',
                sortable: true,
                width: '120px',
                render: (_value, lead) => (
                    <StatusBadge
                        status={lead.status?.name}
                        kind="lead"
                        color={lead.status?.color}
                        label={lead.status?.label}
                    />
                ),
            },
            {
                id: 'country',
                label: 'Country',
                key: 'country',
                sortable: true,
                width: '100px',
                render: value => {
                    const country = value ? getCountryByCode(value) : null;
                    return value ? (
                        <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{country?.name || value}</span>
                        </div>
                    ) : (
                        <span className="text-muted-foreground">-</span>
                    );
                },
            },
            {
                id: 'contact_attempts',
                label: 'Attempts',
                key: 'contact_attempts',
                sortable: true,
                width: '80px',
                render: value => (
                    <div className="text-center">
                        <span className="font-medium">{value || 0}</span>
                    </div>
                ),
            },
            {
                id: 'last_contact',
                label: 'Last Contact',
                key: 'last_contact_date',
                sortable: true,
                width: '140px',
                render: value =>
                    value ? (
                        <DateTimeFormatter
                            date={value}
                            timeZone={crmTimezone}
                            showIcon={true}
                            icon={<Clock className="h-3 w-3 text-muted-foreground" />}
                            className="text-sm"
                        />
                    ) : (
                        <span className="text-muted-foreground">Never</span>
                    ),
            },
            {
                id: 'next_follow_up',
                label: 'Next Follow-up',
                key: 'next_follow_up_date',
                sortable: true,
                width: '140px',
                render: value =>
                    value ? (
                        <DateTimeFormatter
                            date={value}
                            timeZone={crmTimezone}
                            showIcon={true}
                            icon={<Calendar className="h-3 w-3 text-muted-foreground" />}
                            className="text-sm"
                        />
                    ) : (
                        <span className="text-muted-foreground">Not set</span>
                    ),
            },
            {
                id: 'assigned_user',
                label: 'Assigned To',
                key: 'assigned_user_id',
                sortable: true,
                width: '120px',
                render: (_value, lead) =>
                    lead.assigned_user ? (
                        <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm">{lead.assigned_user.name}</span>
                        </div>
                    ) : (
                        <span className="text-muted-foreground">Unassigned</span>
                    ),
            },
            {
                id: 'created_at',
                label: 'Created',
                key: 'created_at',
                sortable: true,
                width: '180px',
                render: value => (
                    <DateTimeFormatter date={value} timeZone={crmTimezone} className="text-sm" />
                ),
            },
            // Add custom attribute columns dynamically
            ...customAttributeDefinitions.map(definition => ({
                id: `custom_${definition.name}`,
                label: definition.label,
                key: `custom_attributes.${definition.name}` as any,
                sortable: true, // Enable sorting for custom attributes
                width: '150px',
                render: (_: any, lead: Lead) => {
                    const customValue = lead.custom_attributes?.[definition.name as any]?.value;
                    if (!customValue) {
                        return <span className="text-muted-foreground">-</span>;
                    }

                    // Format the value based on field type
                    switch (definition.field_type) {
                        case 'boolean':
                            return (
                                <Badge variant={customValue ? 'default' : 'secondary'}>
                                    {customValue ? 'Yes' : 'No'}
                                </Badge>
                            );
                        case 'date':
                            return (
                                <DateTimeFormatter
                                    date={customValue}
                                    timeZone={crmTimezone}
                                    className="text-sm"
                                />
                            );
                        case 'multiselect':
                            return (
                                <div className="text-sm">
                                    {Array.isArray(customValue)
                                        ? customValue.join(', ')
                                        : customValue}
                                </div>
                            );
                        default:
                            return <span className="text-sm">{String(customValue)}</span>;
                    }
                },
            })),
        ],
        [customAttributeDefinitions, crmTimezone, searchParams]
    );

    const createLeadMutation = useCreateLead();
    const deleteLeadMutation = useDeleteLead();
    const exportLeadsMutation = useExportLeads();

    // Computed values
    const leads = leadsData?.data || [];
    const totalLeads = leadsData?.meta?.total || 0;
    const totalPages = Math.ceil(totalLeads / pagination.pageSize);

    // Statistics
    const stats = useMemo(() => {
        // Find status IDs for common statuses
        const newStatus = leadStatuses.find(status => status.name === 'new');
        const qualifiedStatus = leadStatuses.find(status => status.name === 'qualified');
        const convertedStatus = leadStatuses.find(status => status.name === 'converted');

        const newLeads = newStatus
            ? leads.filter((lead: Lead) => lead.status?.id === newStatus.id).length
            : 0;
        const qualifiedLeads = qualifiedStatus
            ? leads.filter((lead: Lead) => lead.status?.id === qualifiedStatus.id).length
            : 0;
        const convertedLeads = convertedStatus
            ? leads.filter((lead: Lead) => lead.status?.id === convertedStatus.id).length
            : 0;
        const highPriorityLeads = 0; // Priority field removed

        return {
            total: totalLeads,
            new: newLeads,
            qualified: qualifiedLeads,
            converted: convertedLeads,
            highPriority: highPriorityLeads,
        };
    }, [leads, totalLeads, leadStatuses]);

    // Event handlers
    const handleCreateLead = (data: CreateLeadRequest) => {
        setCreateErrors({});
        createLeadMutation.mutate(data, {
            onSuccess: () => {
                enqueueSnackbar('Lead created successfully', { variant: 'success' });
                setCreateDialogOpen(false);
                setCreateErrors({});
            },
            onError: (error: any) => {
                if (error.response?.data?.errors) {
                    setCreateErrors(error.response.data.errors);
                    const errorMessages = Object.values(error.response.data.errors).flat();
                    errorMessages.forEach((message: any) => {
                        enqueueSnackbar(String(message), { variant: 'error' });
                    });
                } else {
                    enqueueSnackbar('Failed to create lead', { variant: 'error' });
                }
            },
        });
    };

    const handleDeleteLead = () => {
        if (deletingLead) {
            deleteLeadMutation.mutate(deletingLead.id, {
                onSuccess: () => {
                    enqueueSnackbar('Lead deleted successfully', { variant: 'success' });
                    setDeleteDialogOpen(false);
                    setDeletingLead(null);
                },
                onError: () => {
                    enqueueSnackbar('Failed to delete lead', { variant: 'error' });
                },
            });
        }
    };

    // Exports every lead matching the current view (all pages), or only the given rows
    const handleExportLeads = (selectedIds?: string[]) => {
        if (exportLeadsMutation.isPending) {
            return;
        }

        const exportFilters = selectedIds?.length
            ? { selected_ids: selectedIds }
            : buildApiParams(viewParams);

        exportLeadsMutation.mutate(
            { format: 'csv', filters: exportFilters },
            {
                onSuccess: () => {
                    enqueueSnackbar('Leads exported successfully', { variant: 'success' });
                },
                onError: () => {
                    enqueueSnackbar('Failed to export leads', { variant: 'error' });
                },
            }
        );
    };

    const handleSort = (key: keyof Lead) => {
        toggleSort(key as string);
    };

    const handleFilterChange = (newFilters: any) => {
        updateFilters(newFilters);
    };

    const defaultVisibleLeadColumns = useMemo(
        () => columns.map(col => col.id).filter(id => id !== 'customer_number'),
        [columns]
    );

    // Column preferences with localStorage persistence
    const { visibleColumns, handleColumnToggle, handleResetColumns, setVisibleColumnsFromView } =
        useColumnPreferences({
            storageKey: 'leads-page-visible-columns',
            defaultColumns: defaultVisibleLeadColumns,
            columns,
        });

    const handleClearFilters = () => {
        clearFilters();
        handleResetColumns();
    };

    // Views management
    const {
        views,
        isLoading: isLoadingViews,
        selectedViewId,
        handleViewSelect,
        handleSaveAsView,
        handleUpdateView,
        handleDeleteView,
        handleReorderViews,
        isSavingView,
        isAdmin: isAdminUser,
    } = usePageViews({
        module: 'leads',
        currentFilters: filters,
        currentSort: sort,
        currentVisibleColumns: visibleColumns,
        defaultColumns: columns.map(col => col.id),
        currentPageSize: pagination.pageSize,
        onApplyViewState: ({
            filters: nextFilters,
            sort: nextSort,
            pagination: nextPagination,
        }) => {
            replaceState({
                filters: nextFilters,
                sort: nextSort ?? undefined,
                pagination: nextPagination,
            });
        },
        onFiltersChange: updateFilters,
        onSortChange: ({ key, direction }) => {
            if (sort.key !== key) {
                toggleSort(key);
                if (direction === 'desc') {
                    setTimeout(() => toggleSort(key), 0);
                }
            } else if (sort.direction !== direction) {
                toggleSort(key);
            }
        },
        onVisibleColumnsChange: cols => {
            setVisibleColumnsFromView(cols);
        },
    });

    // Page size change handler
    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    // Bulk actions handlers
    const bulkUpdateStatusMutation = useBulkUpdateLeadStatus();
    const bulkAssignMutation = useBulkAssignLeads();
    const bulkDeleteMutation = useBulkDeleteLeads();
    const bulkConvertMutation = useBulkConvertLeadsToCustomers();

    const handleBulkStatusChange = () => {
        if (selectedLeads.length === 0) return;
        setBulkStatusDialogOpen(true);
    };

    const handleBulkAssign = () => {
        if (selectedLeads.length === 0) return;
        setBulkAssignDialogOpen(true);
    };

    const handleBulkStatusUpdate = () => {
        if (!selectedStatusId) return;
        bulkUpdateStatusMutation.mutate(
            { leadIds: selectedLeads, statusId: selectedStatusId },
            {
                onSuccess: () => {
                    enqueueSnackbar(
                        `Successfully updated status for ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''}`,
                        { variant: 'success', autoHideDuration: 3000 }
                    );
                    setSelectedLeads([]);
                    setBulkStatusDialogOpen(false);
                    setSelectedStatusId('');
                },
                onError: (error: any) => {
                    console.error('Bulk status update error:', error);
                    const errors = error.response?.data?.errors;
                    const errorMessage =
                        errors?.status_id?.[0] ||
                        errors?.lead_ids?.[0] ||
                        error.response?.data?.message ||
                        error.response?.data?.error ||
                        error.message ||
                        'Failed to update lead statuses';
                    enqueueSnackbar(errorMessage, {
                        variant: 'error',
                        autoHideDuration: 5000,
                    });
                },
            }
        );
    };

    const handleBulkAssignUpdate = () => {
        if (selectedUserId === '' || selectedUserId === undefined) return;
        const isUnassign = selectedUserId === '__unassigned__';
        bulkAssignMutation.mutate(
            {
                leadIds: selectedLeads,
                assignedUserId: isUnassign ? null : selectedUserId,
            },
            {
                onSuccess: (_, variables) => {
                    const isUnassign = variables.assignedUserId === null;
                    enqueueSnackbar(
                        isUnassign
                            ? `Successfully unassigned ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''}`
                            : `Successfully assigned ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''} to user`,
                        { variant: 'success', autoHideDuration: 3000 }
                    );
                    setSelectedLeads([]);
                    setBulkAssignDialogOpen(false);
                    setSelectedUserId('');
                },
                onError: (error: any) => {
                    console.error('Bulk assign error:', error);
                    const errorMessage =
                        error.response?.data?.error || error.message || 'Failed to assign leads';
                    enqueueSnackbar(errorMessage, {
                        variant: 'error',
                        autoHideDuration: 5000,
                    });
                },
            }
        );
    };

    const handleBulkDelete = async () => {
        if (selectedLeads.length === 0) return;

        const ok = await confirm({
            title: 'Delete leads?',
            description: `${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''} will be permanently deleted. This action cannot be undone.`,
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;

        bulkDeleteMutation.mutate(selectedLeads, {
            onSuccess: () => {
                enqueueSnackbar(
                    `Successfully deleted ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''}`,
                    { variant: 'success', autoHideDuration: 3000 }
                );
                setSelectedLeads([]);
            },
            onError: (error: any) => {
                console.error('Bulk delete error:', error);
                const errorMessage =
                    error.response?.data?.error || error.message || 'Failed to delete leads';
                enqueueSnackbar(errorMessage, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            },
        });
    };

    const handleBulkConvert = async () => {
        if (selectedLeads.length === 0) return;

        const ok = await confirm({
            title: 'Convert leads to customers?',
            description: `${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''} will be converted to customer${selectedLeads.length !== 1 ? 's' : ''}.`,
            confirmLabel: 'Convert',
        });
        if (!ok) return;

        bulkConvertMutation.mutate(selectedLeads, {
            onSuccess: () => {
                enqueueSnackbar(
                    `Successfully converted ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''} to customer${selectedLeads.length !== 1 ? 's' : ''}`,
                    { variant: 'success', autoHideDuration: 3000 }
                );
                setSelectedLeads([]);
            },
            onError: (error: any) => {
                console.error('Bulk convert error:', error);
                const errorMessage =
                    error.response?.data?.error ||
                    error.message ||
                    'Failed to convert leads to customers';
                enqueueSnackbar(errorMessage, {
                    variant: 'error',
                    autoHideDuration: 5000,
                });
            },
        });
    };

    // Define filter fields for the GenericDataTable component
    const filterFields = useMemo(
        () => [
            {
                key: 'search',
                label: 'Search',
                type: 'text' as const,
                placeholder: 'Search leads...',
            },
            {
                key: 'status',
                label: 'Status',
                type: 'select' as const,
                options: leadStatuses.map(s => ({ value: s.id, label: s.label })),
            },
            {
                key: 'lead_source',
                label: 'Source',
                type: 'select' as const,
                options: [
                    { value: 'website', label: 'Website' },
                    { value: 'referral', label: 'Referral' },
                    { value: 'social_media', label: 'Social Media' },
                    { value: 'email_campaign', label: 'Email Campaign' },
                    { value: 'cold_call', label: 'Cold Call' },
                    { value: 'event', label: 'Event' },
                    { value: 'other', label: 'Other' },
                ],
            },
            {
                key: 'assigned_user_id',
                label: 'Assigned To',
                type: 'select' as const,
                options: [
                    { value: 'unassigned', label: 'Unassigned' },
                    ...(users && users.length > 0
                        ? users.map(user => ({ value: user.id, label: user.name }))
                        : []),
                ],
            },
            {
                key: 'country',
                label: 'Country',
                type: 'select' as const,
                options: countries.map(country => ({ value: country.name, label: country.name })),
            },
            {
                key: 'min_score',
                label: 'Min Score',
                type: 'number' as const,
                placeholder: '0',
                width: 'min-w-[100px]',
            },
            {
                key: 'max_score',
                label: 'Max Score',
                type: 'number' as const,
                placeholder: '100',
                width: 'min-w-[100px]',
            },
            {
                key: 'min_conversion_potential',
                label: 'Min Conversion %',
                type: 'number' as const,
                placeholder: '0',
                width: 'min-w-[100px]',
            },
            {
                key: 'max_conversion_potential',
                label: 'Max Conversion %',
                type: 'number' as const,
                placeholder: '100',
                width: 'min-w-[100px]',
            },
            {
                key: 'contact_attempts_min',
                label: 'Min Attempts',
                type: 'number' as const,
                placeholder: '0',
                width: 'min-w-[100px]',
            },
            {
                key: 'contact_attempts_max',
                label: 'Max Attempts',
                type: 'number' as const,
                placeholder: '10',
                width: 'min-w-[100px]',
            },
            { key: 'last_contact_after', label: 'Last Contact After', type: 'date' as const },
            { key: 'last_contact_before', label: 'Last Contact Before', type: 'date' as const },
            { key: 'next_follow_up_after', label: 'Next Follow-up After', type: 'date' as const },
            { key: 'next_follow_up_before', label: 'Next Follow-up Before', type: 'date' as const },
            {
                key: 'has_contact',
                label: 'Has Contact',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
            {
                key: 'has_follow_up',
                label: 'Has Follow-up',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
            {
                key: 'has_phone',
                label: 'Has Phone',
                type: 'boolean' as const,
                options: [
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                ],
            },
            // Add custom attribute filters dynamically
            ...customAttributeDefinitions.map(definition => ({
                key: `custom_attributes.${definition.name}`,
                label: definition.label,
                type:
                    definition.field_type === 'boolean'
                        ? ('boolean' as const)
                        : definition.field_type === 'date'
                          ? ('date' as const)
                          : definition.field_type === 'number'
                            ? ('number' as const)
                            : definition.field_type === 'select' ||
                                definition.field_type === 'multiselect'
                              ? ('select' as const)
                              : ('text' as const),
                placeholder:
                    definition.field_type === 'text'
                        ? `Search ${definition.label.toLowerCase()}...`
                        : undefined,
                options:
                    definition.field_type === 'select' || definition.field_type === 'multiselect'
                        ? definition.options?.map(option => ({ value: option, label: option }))
                        : undefined,
            })),
        ],
        [leadStatuses, customAttributeDefinitions, users]
    );

    // Filter options are now handled by the LeadsFilters component

    /** Drives the empty state: "no matches" reads differently from "nothing yet". */
    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Leads"
                description="Manage and track your sales leads"
                actions={
                    <>
                        {hasPermission(ModulePermission.LEADS, PermissionAction.EDIT) && (
                            <Button variant="outline" onClick={() => setDuplicatesDialogOpen(true)}>
                                <GitMerge className="h-4 w-4" />
                                Duplicates
                            </Button>
                        )}
                        {hasPermission(ModulePermission.LEADS, PermissionAction.IMPORT) && (
                            <Button variant="outline" onClick={() => setEnhancedImportOpen(true)}>
                                <Upload className="h-4 w-4" />
                                Import
                            </Button>
                        )}
                        {hasPermission(ModulePermission.LEADS, PermissionAction.CREATE) && (
                            <Button onClick={() => setCreateDialogOpen(true)}>
                                <Plus className="h-4 w-4" />
                                Add Lead
                            </Button>
                        )}
                    </>
                }
            />

            <StatTileGrid className="shrink-0 xl:grid-cols-5">
                <StatTile
                    label="Total Leads"
                    value={stats.total}
                    icon={Target}
                    tone="brand"
                    isLoading={isLoading}
                />
                <StatTile
                    label="New"
                    value={stats.new}
                    icon={User}
                    tone="info"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Qualified"
                    value={stats.qualified}
                    icon={TrendingUp}
                    tone="success"
                    isLoading={isLoading}
                />
                <StatTile
                    label="Converted"
                    value={stats.converted}
                    icon={Calendar}
                    tone="success"
                    isLoading={isLoading}
                />
                <StatTile
                    label="High Priority"
                    value={stats.highPriority}
                    icon={BarChart3}
                    tone="destructive"
                    isLoading={isLoading}
                />
            </StatTileGrid>

            {/* Bulk Actions Toolbar */}
            {selectedLeads.length > 0 && (
                <div className="flex shrink-0 items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                            {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''}{' '}
                            selected
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {hasPermission(ModulePermission.LEADS, PermissionAction.EDIT) && (
                            <Button variant="outline" size="sm" onClick={handleBulkStatusChange}>
                                <Edit className="h-4 w-4" />
                                Change Status
                            </Button>
                        )}
                        {hasPermission(ModulePermission.LEADS, PermissionAction.EDIT) && (
                            <Button variant="outline" size="sm" onClick={handleBulkAssign}>
                                <UserPlus className="h-4 w-4" />
                                Change Assignee
                            </Button>
                        )}
                        {hasPermission(ModulePermission.CUSTOMERS, PermissionAction.CREATE) && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleBulkConvert}
                                disabled={bulkConvertMutation.isPending}
                                className="text-success hover:bg-success/10 hover:text-success"
                            >
                                <ArrowRight className="h-4 w-4" />
                                {bulkConvertMutation.isPending
                                    ? 'Converting...'
                                    : 'Convert to Customer'}
                            </Button>
                        )}
                        {hasPermission(ModulePermission.LEADS, PermissionAction.EXPORT) && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleExportLeads(selectedLeads)}
                                disabled={exportLeadsMutation.isPending}
                            >
                                <Download className="h-4 w-4" />
                                {exportLeadsMutation.isPending ? 'Exporting...' : 'Export Selected'}
                            </Button>
                        )}
                        {hasPermission(ModulePermission.LEADS, PermissionAction.DELETE) && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleBulkDelete}
                                disabled={bulkDeleteMutation.isPending}
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            >
                                <Trash2 className="h-4 w-4" />
                                {bulkDeleteMutation.isPending ? 'Deleting...' : 'Delete'}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* View Selector */}
            <ViewSelector
                views={views}
                selectedViewId={selectedViewId}
                onViewSelect={handleViewSelect}
                onSaveAsView={handleSaveAsView}
                onUpdateView={handleUpdateView}
                onDeleteView={handleDeleteView}
                onReorderViews={handleReorderViews}
                isSavingView={isSavingView}
                isAdmin={isAdminUser}
                isLoading={isLoadingViews}
                filterFields={filterFields}
                columns={columns}
            />

            {/* Generic Data Table */}
            <GenericDataTable<Lead>
                data={leads}
                columns={columns}
                visibleColumns={visibleColumns}
                filters={filters}
                filterFields={filterFields}
                presets={[]}
                isLoading={isLoading || isLoadingCustomAttributeDefinitions}
                selectedItems={selectedLeads}
                onSelectionChange={setSelectedLeads}
                getItemId={lead => lead.id}
                sortConfig={{ key: sort.key as keyof Lead, direction: sort.direction }}
                onSort={handleSort}
                totalItems={totalLeads}
                totalPages={totalPages}
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onExport={() => handleExportLeads()}
                isExporting={exportLeadsMutation.isPending}
                showExportButton={hasPermission(ModulePermission.LEADS, PermissionAction.EXPORT)}
                onRowClick={lead => {
                    // Keep the exact same URL format as the list (filter_*, sort, direction, pageSize, page)
                    // so the detail URL is consistent and "back to list" works correctly.
                    const queryString = searchParams.toString();
                    navigate(`/leads/${lead.id}${queryString ? `?${queryString}` : ''}`);
                }}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onColumnToggle={handleColumnToggle}
                onResetColumns={handleResetColumns}
                emptyMessage="No leads found"
                loadingMessage="Loading leads…"
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={Target}
                        title="No leads found"
                        description={
                            hasActiveFilters
                                ? 'No lead matches the current filters. Try clearing them.'
                                : 'Leads will appear here once they come in or you add them.'
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                                    Clear filters
                                </Button>
                            ) : hasPermission(ModulePermission.LEADS, PermissionAction.CREATE) ? (
                                <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                                    <Plus className="h-4 w-4" />
                                    Add Lead
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />

            {/* Create Lead Dialog */}
            <Dialog
                open={createDialogOpen}
                onOpenChange={open => {
                    setCreateDialogOpen(open);
                    if (!open) setCreateErrors({});
                }}
            >
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Create New Lead</DialogTitle>
                    </DialogHeader>
                    <LeadForm
                        onSubmit={handleCreateLead}
                        onCancel={() => setCreateDialogOpen(false)}
                        isLoading={createLeadMutation.isPending}
                        mode="create"
                        serverErrors={createErrors}
                    />
                </DialogContent>
            </Dialog>

            {/* Enhanced Import Wizard */}
            <EnhancedImportWizard
                isOpen={enhancedImportOpen}
                onClose={() => {
                    setEnhancedImportOpen(false);
                }}
                onImportComplete={() => {
                    // Refresh the leads list
                    // The wizard will close itself
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Lead</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to delete this lead? This action cannot be undone.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteLead}
                            disabled={deleteLeadMutation.isPending}
                        >
                            {deleteLeadMutation.isPending ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <LeadDuplicatesDialog
                open={duplicatesDialogOpen}
                onOpenChange={setDuplicatesDialogOpen}
            />

            {/* Bulk Status Change Dialog */}
            <Dialog open={bulkStatusDialogOpen} onOpenChange={setBulkStatusDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Change Status for {selectedLeads.length} Lead
                            {selectedLeads.length !== 1 ? 's' : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Select New Status</label>
                            <Select value={selectedStatusId} onValueChange={setSelectedStatusId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Choose a status..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {leadStatuses.map(status => (
                                        <SelectItem key={status.id} value={status.id}>
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{ backgroundColor: status.color }}
                                                />
                                                {status.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBulkStatusDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleBulkStatusUpdate}
                            disabled={!selectedStatusId || bulkUpdateStatusMutation.isPending}
                        >
                            {bulkUpdateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Assign Dialog */}
            <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Assign {selectedLeads.length} Lead
                            {selectedLeads.length !== 1 ? 's' : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Select User</label>
                            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Choose a user..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__unassigned__">
                                        <div className="flex items-center gap-2">
                                            <User className="h-4 w-4" />
                                            Unassigned
                                        </div>
                                    </SelectItem>
                                    {users.map(user => (
                                        <SelectItem key={user.id} value={user.id}>
                                            <div className="flex items-center gap-2">
                                                <User className="h-4 w-4" />
                                                {user.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBulkAssignDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleBulkAssignUpdate}
                            disabled={
                                selectedUserId === '' ||
                                selectedUserId === undefined ||
                                bulkAssignMutation.isPending
                            }
                        >
                            {bulkAssignMutation.isPending
                                ? selectedUserId === '__unassigned__'
                                    ? 'Unassigning...'
                                    : 'Assigning...'
                                : selectedUserId === '__unassigned__'
                                  ? 'Unassign'
                                  : 'Assign'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default LeadsPage;
